import React, { useEffect, useState } from 'react';
import Box from '@components/Atoms/Box';
import Typography from '@components/Atoms/Typography';
import Skeleton from '@components/Atoms/Skeleton';
import ScrollWrapper from '@components/Atoms/ScrollWrapper';
import InformationCard from '@components/Component/InformationCard';
import { MenuItem, Select, FormControl, InputLabel, Tabs, Tab } from '@mui/material';
import {
  usePMOutcomeReport,
  usePMOutcomeDetail,
  PMDistrictRequiredError,
  readStoredPMDistrictId,
  storePMDistrictId,
} from './hooks';
import { PMDistrictOption } from './types';
import PMOutcomeHierarchy from './PMOutcomeHierarchy';
import { PMSubjectReport, PMDetailView } from './types';
import {
  buildSummaryColumns,
  buildBloomsColumns,
  buildStandardsColumns,
  ScoreMode,
} from './columns';
import BandEditor, { BandInput } from './BandEditor';
import PMExportExcelButton from './PMExportExcelButton';

const NO_COMPARE = '__none__';

type SubTab = 'summary' | 'raw' | 'blooms' | 'standards';

const SUB_TABS: Array<{ key: SubTab; label: string }> = [
  { key: 'summary', label: 'Summary' },
  { key: 'raw', label: 'Summary Raw' },
  { key: 'blooms', label: 'Blooms' },
  { key: 'standards', label: 'Standards' },
];

const DetailHierarchy: React.FC<{
  districtId: number;
  year: string | null;
  period: string;
  subject: string;
  view: PMDetailView;
  districtName: string;
}> = ({ districtId, year, period, subject, view, districtName }) => {
  const { data, isLoading, error } = usePMOutcomeDetail(
    districtId, year, period, subject, view,
  );

  if (isLoading && !data) {
    return <Skeleton height="16rem" width="100%" />;
  }
  if (error || !data) {
    return (
      <InformationCard
        img="/images/500_error.svg"
        title={`Could not load ${view} breakdown`}
        description={error instanceof Error ? error.message : 'An unexpected error occurred.'}
      />
    );
  }
  const leaves = view === 'blooms' ? buildBloomsColumns(data) : buildStandardsColumns(data);
  return (
    <PMOutcomeHierarchy
      data={data}
      districtName={districtName}
      bands={data.bands}
      leaves={leaves}
    />
  );
};

const PMOutcomesPage: React.FC = () => {
  const [districtId, setDistrictId] = useState<number | null>(readStoredPMDistrictId);
  const [districtOptions, setDistrictOptions] = useState<PMDistrictOption[]>([]);
  const [year, setYear] = useState<string | null>(null);
  const [period, setPeriod] = useState<string | null>(null);
  const [compare, setCompare] = useState<string | null>(null);
  const [customBands, setCustomBands] = useState<BandInput[] | null>(null);
  const [subjectIndex, setSubjectIndex] = useState(0);
  const [subTab, setSubTab] = useState<SubTab>('summary');

  const { data, isLoading, error } = usePMOutcomeReport(
    districtId, year, period, compare, customBands,
  );

  const handleDistrictChange = (nextDistrictId: number): void => {
    setDistrictId(nextDistrictId);
    storePMDistrictId(nextDistrictId);
    setYear(null);
    setPeriod(null);
    setCompare(null);
  };

  useEffect(() => {
    if (error instanceof PMDistrictRequiredError) {
      setDistrictOptions(error.districts);
    }
  }, [error]);

  useEffect(() => {
    if (data?.districts?.length) {
      setDistrictOptions(data.districts);
    }
  }, [data?.districts]);

  // Subject tabs are index-based; different periods can expose different
  // subject lists (e.g. Algebra 1-A only exists in PM3)
  useEffect(() => {
    setSubjectIndex(0);
  }, [data?.period]);

  if (isLoading && !data) {
    return (
      <Box sx={{ p: 4, display: 'flex', flexDirection: 'column', gap: 2 }}>
        <Typography variant="h6">Loading progress monitoring outcomes…</Typography>
        <Skeleton height="24rem" width="100%" />
      </Box>
    );
  }

  if (error instanceof PMDistrictRequiredError) {
    const districts = error.districts;
    return (
      <Box sx={{ p: 4, display: 'flex', flexDirection: 'column', gap: 2, maxWidth: '28rem' }}>
        <Typography variant="h6">Select a district</Typography>
        <Typography variant="body2" color="text.secondary">
          Your account administers multiple districts. Choose one to load progress monitoring
          outcomes.
        </Typography>
        <FormControl size="small" fullWidth>
          <InputLabel id="pm-district-label">District</InputLabel>
          <Select
            labelId="pm-district-label"
            label="District"
            value={districtId ?? ''}
            onChange={(e) => {
              setDistrictOptions(districts);
              handleDistrictChange(Number(e.target.value));
            }}
          >
            {districts.map((district) => (
              <MenuItem key={district.id} value={district.id}>
                {district.name}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Box>
    );
  }

  if (error || !data) {
    return (
      <InformationCard
        img="/images/500_error.svg"
        title="Could not load progress monitoring outcomes"
        description={
          error instanceof Error ? error.message : 'An unexpected error occurred.'
        }
      />
    );
  }

  const periodsWithData = data.periods.filter((p) => p.has_data);
  const comparablePeriods = periodsWithData.filter((p) => p.period !== data.period);
  const subjects = data.subjects;
  const activeSubject: PMSubjectReport | undefined =
    subjects[Math.min(subjectIndex, subjects.length - 1)];

  return (
    <ScrollWrapper sx={{ height: '100%', width: '100%' }}>
      <Box sx={{ p: 3, display: 'flex', flexDirection: 'column', gap: 2 }}>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            flexWrap: 'wrap',
            gap: 2,
          }}
        >
          <Box>
            <Typography variant="h5" component="h1" sx={{ fontWeight: 'bold' }}>
              Progress Monitoring Outcomes
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {data.district_name}
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
            {districtOptions.length > 1 && (
              <FormControl size="small" sx={{ minWidth: '14rem' }}>
                <InputLabel id="pm-district-toolbar-label">District</InputLabel>
                <Select
                  labelId="pm-district-toolbar-label"
                  label="District"
                  value={data.district_id}
                  onChange={(e) => handleDistrictChange(Number(e.target.value))}
                >
                  {districtOptions.map((district) => (
                    <MenuItem key={district.id} value={district.id}>
                      {district.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            )}
            <BandEditor
              current={data.bands}
              defaults={data.default_bands}
              isCustomized={!!customBands}
              onApply={setCustomBands}
            />
            {data.scope !== 'teacher' && data.years.length > 0 && (
              <FormControl size="small" sx={{ minWidth: '9rem' }}>
                <InputLabel id="pm-year-label">Year</InputLabel>
                <Select
                  labelId="pm-year-label"
                  label="Year"
                  value={data.year ?? data.years[data.years.length - 1]}
                  onChange={(e) => {
                    setYear(e.target.value);
                    setPeriod(null);
                    setCompare(null);
                  }}
                >
                  {data.years.map((y) => (
                    <MenuItem key={y} value={y}>
                      {y}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            )}
            <FormControl size="small" sx={{ minWidth: '8rem' }}>
              <InputLabel id="pm-period-label">Period</InputLabel>
              <Select
                labelId="pm-period-label"
                label="Period"
                value={data.period}
                onChange={(e) => {
                  setPeriod(e.target.value);
                  if (compare === e.target.value) setCompare(null);
                }}
              >
                {periodsWithData.map((p) => (
                  <MenuItem key={p.period} value={p.period}>
                    {p.period}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl size="small" sx={{ minWidth: '10rem' }}>
              <InputLabel id="pm-compare-label">Growth vs</InputLabel>
              <Select
                labelId="pm-compare-label"
                label="Growth vs"
                value={data.compare_period ?? NO_COMPARE}
                onChange={(e) =>
                  setCompare(e.target.value === NO_COMPARE ? null : e.target.value)
                }
              >
                <MenuItem value={NO_COMPARE}>None</MenuItem>
                {comparablePeriods.map((p) => (
                  <MenuItem key={p.period} value={p.period}>
                    {p.period}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <PMExportExcelButton
              districtId={data.district_id}
              year={data.year}
              period={data.period}
              compare={data.compare_period}
              bands={customBands ?? data.bands}
              subject={activeSubject?.subject ?? ''}
              view={subTab}
              disabled={subjects.length === 0 || !activeSubject}
            />
          </Box>
        </Box>

        {subjects.length === 0 && (
          <InformationCard
            img="/images/no-content.png"
            title="No progress monitoring data"
            description="No scored progress monitoring results were found for this period."
          />
        )}

        {subjects.length > 0 && activeSubject && (
          <>
            <Tabs
              value={Math.min(subjectIndex, subjects.length - 1)}
              onChange={(_e, value) => setSubjectIndex(value)}
              variant="scrollable"
              scrollButtons="auto"
            >
              {subjects.map((subject) => (
                <Tab key={subject.subject} label={subject.subject} />
              ))}
            </Tabs>

            <Tabs
              value={subTab}
              onChange={(_e, value) => setSubTab(value)}
              variant="scrollable"
              scrollButtons="auto"
              sx={{ minHeight: '2.25rem', '& .MuiTab-root': { minHeight: '2.25rem', py: 0.5 } }}
            >
              {SUB_TABS.map((tab) => (
                <Tab key={tab.key} value={tab.key} label={tab.label} />
              ))}
            </Tabs>

            {(subTab === 'summary' || subTab === 'raw') && (
              <>
                <PMOutcomeHierarchy
                  data={activeSubject}
                  districtName={data.district_name}
                  bands={data.bands}
                  leaves={buildSummaryColumns(
                    activeSubject,
                    data.period,
                    data.compare_period,
                    data.bands,
                    (subTab === 'raw' ? 'raw' : 'weighted') as ScoreMode,
                  )}
                />
                <Typography variant="caption" color="text.secondary">
                  {subTab === 'raw'
                    ? 'Raw (unweighted) percentages: correct answers over questions seen.'
                    : "Scores are Blooms-weighted percentages of the period's fixed denominators."}{' '}
                  Click a row to expand the next level.
                  {data.compare_period
                    ? ` Growth shown in parentheses vs ${data.compare_period}.`
                    : ''}
                </Typography>
              </>
            )}

            {(subTab === 'blooms' || subTab === 'standards') && (
              <>
                <DetailHierarchy
                  districtId={data.district_id}
                  year={data.year}
                  period={data.period}
                  subject={activeSubject.subject}
                  view={subTab}
                  districtName={data.district_name}
                />
                <Typography variant="caption" color="text.secondary">
                  {subTab === 'blooms'
                    ? 'Success rate per Blooms level (correct over attempted; hover a value for counts). No Answer counts served-but-unanswered questions.'
                    : 'Success rate per standard (hover a column header for the standard description, a value for counts).'}
                </Typography>
              </>
            )}
          </>
        )}
      </Box>
    </ScrollWrapper>
  );
};

export default PMOutcomesPage;
